/**
 * Settings Service
 * API service for Settings page (RBAC, AI Providers, Organization, etc.)
 */

import { apiFetch, createApiHeaders, buildApiUrl } from '../config/api';

class SettingsService {
  constructor() {
    this.baseUrl = '/api/v1/settings';
  }

  // ============================================================================
  // USER MANAGEMENT
  // ============================================================================

  /**
   * List users with pagination and filters
   * Returns users with their role information
   */
  async listUsers(params = {}) {
    const { page = 1, pageSize = 20, search = '', status = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      ...(search && { search }),
      ...(status && { status }),
    });

    return apiFetch(`${this.baseUrl}/users?${queryParams}`);
  }

  /**
   * Get user details by ID
   */
  async getUser(userId) {
    return apiFetch(`${this.baseUrl}/users/${userId}`);
  }

  /**
   * Create new user
   */
  async createUser(userData) {
    return apiFetch(`${this.baseUrl}/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Update user
   */
  async updateUser(userId, userData) {
    return apiFetch(`${this.baseUrl}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Delete user
   */
  async deleteUser(userId) {
    return apiFetch(`${this.baseUrl}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Assign role to user
   */
  async assignRoleToUser(userId, roleId) {
    return apiFetch(`${this.baseUrl}/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId }),
    });
  }

  /**
   * Remove role from user
   */
  async removeRoleFromUser(userId, roleId) {
    return apiFetch(`${this.baseUrl}/users/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get user permissions
   */
  async getUserPermissions(userId) {
    return apiFetch(`${this.baseUrl}/users/${userId}/permissions`);
  }

  // ============================================================================
  // ROLE MANAGEMENT
  // ============================================================================

  /**
   * List all roles (system + custom)
   */
  async listRoles() {
    return apiFetch(`${this.baseUrl}/roles`);
  }

  /**
   * Get role details
   */
  async getRole(roleId) {
    return apiFetch(`${this.baseUrl}/roles/${roleId}`);
  }

  /**
   * Create custom role
   */
  async createRole(roleData) {
    return apiFetch(`${this.baseUrl}/roles`, {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  }

  /**
   * Update custom role
   */
  async updateRole(roleId, roleData) {
    return apiFetch(`${this.baseUrl}/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    });
  }

  /**
   * Delete custom role
   */
  async deleteRole(roleId) {
    return apiFetch(`${this.baseUrl}/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Assign permissions to role
   */
  async assignPermissionsToRole(roleId, permissionCodes) {
    return apiFetch(`${this.baseUrl}/roles/${roleId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permission_codes: permissionCodes }),
    });
  }

  /**
   * Remove permissions from role
   */
  async removePermissionsFromRole(roleId, permissionCodes) {
    return apiFetch(`${this.baseUrl}/roles/${roleId}/permissions`, {
      method: 'DELETE',
      body: JSON.stringify({ permission_codes: permissionCodes }),
    });
  }

  // ============================================================================
  // PERMISSION MANAGEMENT
  // ============================================================================

  /**
   * List all permissions
   */
  async listPermissions() {
    return apiFetch(`${this.baseUrl}/permissions`);
  }

  /**
   * List permissions grouped by resource
   */
  async listPermissionsByResource() {
    return apiFetch(`${this.baseUrl}/permissions/by-resource`);
  }

  // ============================================================================
  // AI PROVIDER MANAGEMENT
  // ============================================================================

  /**
   * List AI providers
   * @param {boolean} forceRefresh - Force refresh from AWS (bypass cache)
   */
  async listAIProviders(forceRefresh = false) {
    const url = forceRefresh
      ? `${this.baseUrl}/ai-providers?force_refresh=true`
      : `${this.baseUrl}/ai-providers`;
    return apiFetch(url);
  }

  /**
   * Get AI provider details
   */
  async getAIProvider(providerId) {
    return apiFetch(`${this.baseUrl}/ai-providers/${providerId}`);
  }

  /**
   * Get AI provider with full API key (requires special permission)
   */
  async getAIProviderWithKey(providerId) {
    return apiFetch(`${this.baseUrl}/ai-providers/${providerId}/key`);
  }

  /**
   * Create AI provider
   */
  async createAIProvider(providerData) {
    return apiFetch(`${this.baseUrl}/ai-providers`, {
      method: 'POST',
      body: JSON.stringify(providerData),
    });
  }

  /**
   * Update AI provider in AWS Secrets Manager
   */
  async updateAIProvider(providerId, providerData) {
    // URL encode provider_id to handle special characters like colons (e.g., "openai:gpt-4")
    const encodedProviderId = encodeURIComponent(providerId);
    return apiFetch(`${this.baseUrl}/ai-providers/${encodedProviderId}`, {
      method: 'PUT',
      body: JSON.stringify(providerData),
    });
  }

  /**
   * Delete AI provider
   */
  async deleteAIProvider(providerId) {
    // URL encode provider_id to handle special characters like colons (e.g., "openai:gpt-4")
    const encodedProviderId = encodeURIComponent(providerId);
    return apiFetch(`${this.baseUrl}/ai-providers/${encodedProviderId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // ORGANIZATION SETTINGS
  // ============================================================================

  /**
   * Get organization settings
   */
  async getOrganizationSettings() {
    return apiFetch(`${this.baseUrl}/organization`);
  }

  /**
   * Update organization settings
   */
  async updateOrganizationSettings(settingsData) {
    return apiFetch(`${this.baseUrl}/organization`, {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  // ============================================================================
  // API KEY MANAGEMENT
  // ============================================================================

  /**
   * List API keys
   */
  async listAPIKeys() {
    return apiFetch(`${this.baseUrl}/api-keys`);
  }

  /**
   * Create/generate new API key
   */
  async createAPIKey(keyData) {
    return apiFetch(`${this.baseUrl}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(keyData),
    });
  }

  /**
   * Update API key (name, scopes, status)
   */
  async updateAPIKey(keyId, updateData) {
    return apiFetch(`${this.baseUrl}/api-keys/${keyId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  /**
   * Delete API key
   */
  async deleteAPIKey(keyId) {
    return apiFetch(`${this.baseUrl}/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // LOGO MANAGEMENT
  // ============================================================================

  /**
   * Get current logo metadata
   */
  async getCurrentLogo() {
    return apiFetch('api/v1/assets/logo/primary/info');
  }

  /**
   * Upload logo
   */
  async uploadLogo(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', 'logo');
    formData.append('asset_key', 'primary');

    // Use buildApiUrl to get the correct API base URL
    const uploadUrl = buildApiUrl('api/v1/assets/upload');

    const headers = createApiHeaders();
    delete headers['Content-Type']; // Remove for FormData uploads

    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Delete logo
   */
  async deleteLogo(assetId) {
    return apiFetch(`api/v1/assets/${assetId}`, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const settingsService = new SettingsService();
export default settingsService;
